import React, { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import haxios from '../lib/haxios';
import * as Tooltip from '@radix-ui/react-tooltip';
import { RotateCw } from 'lucide-react';
import { GlobalContext } from '../context/GlobalContext';

const MessageList = ({ selectedOperator }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const { uinfo } = useContext(GlobalContext);


    const fetchMessages = async () => {
        setLoading(true);
        try {
            const params = {};

            if (uinfo.Status !== 'admin') {
                params.acct = uinfo.Acct;
            }

            const response = await haxios.get('/visiondashboard/messages', { params });

            setMessages(response.data);
            setLastUpdated(new Date());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading && !messages.length) {
        return <div className="w-full p-4 text-center">Loading messages...</div>;
    }

    if (!messages?.length) {
        return <div className="w-full p-4 text-center text-gray-500">No messages found</div>;
    }

    return (
        <Tooltip.Provider>
            <div className="w-full bg-white rounded-lg shadow">
                <div className="flex justify-between items-center px-4 py-3" style={{ backgroundColor: 'royalblue', color: 'white', borderTopLeftRadius: '7px', borderTopRightRadius: '7px' }}>
                    <h2 className="font-bold">LATEST CUSTOMER COMMENTS</h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span style={{ color: 'lightgrey' }}>Last updated: {format(lastUpdated, 'HH:mm:ss')}</span>
                        <button
                            onClick={fetchMessages}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            aria-label="Refresh messages"
                            disabled={loading}
                        >
                            <RotateCw style={{ color: 'lightgrey' }} className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 p-3 text-sm font-semibold bg-gray-100 rounded-t-lg">
                    <div className="col-span-2">Account</div>
                    <div className="col-span-2">Staff</div>
                    <div className="col-span-2">Date</div>
                    <div className="col-span-6">Message</div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                    {messages.map((msg) => (
                        <Tooltip.Root key={msg.id}>
                            <Tooltip.Trigger asChild>
                                <div className="grid grid-cols-12 gap-4 p-3 text-sm hover:bg-gray-50 border-b cursor-pointer">
                                    <div className="col-span-2 truncate">{msg.Acct}</div>
                                    <div className="col-span-2">{msg.staff_member || msg.Op}</div>
                                    <div className="col-span-2">
                                        {format(new Date(msg.timestamp), 'dd/MM/yy HH:mm')}
                                    </div>
                                    <div className="col-span-6 truncate">{msg.message}</div>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Content
                                className="bg-white p-4 rounded-lg shadow-lg border z-50 w-80"
                                side="right"
                            >
                                <div className="space-y-2">
                                    <div><span className="font-semibold">Account:</span> {msg.Acct}</div>
                                    <div><span className="font-semibold">Staff Member:</span> {msg.staff_member || msg.Op}</div>
                                    <div>
                                        <span className="font-semibold">Date:</span>{' '}
                                        {format(new Date(msg.timestamp), 'dd/MM/yyyy HH:mm')}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Message:</span>
                                        <p className="mt-1 whitespace-pre-wrap">{msg.message}</p>
                                    </div>
                                </div>
                                <Tooltip.Arrow className="fill-white" />
                            </Tooltip.Content>
                        </Tooltip.Root>
                    ))}
                </div>
            </div>
        </Tooltip.Provider>
    );
};

export default MessageList;